import React, { useRef, useState } from 'react';
import { Loader2, Sparkles, Upload, X } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import SimpleSelect from './SimpleSelect';

export const EXTRAS_PLATFORMS = ['instagram', 'facebook', 'pinterest', 'youtube'];
export const ALT_TEXT_PLATFORMS = ['instagram', 'facebook', 'pinterest'];

/** UI state -> `platform_options` the backend stores (null-ish keys omitted). */
export function toPlatformOptionsPayload(platform, o, kind) {
  const out = {};
  if (platform === 'instagram') {
    if (o.contentType === 'story') out.contentType = 'story';
    if (o.isAiGenerated) out.isAiGenerated = true;
    if (o.contentType !== 'story') {
      const collab = (o.collaboratorsText || '').split(/[\s,]+/).map((c) => c.replace(/^@/, '').trim()).filter(Boolean);
      if (collab.length) out.collaborators = collab;
      if (kind === 'video') {
        if (o.coverUrl) out.coverUrl = o.coverUrl;
        else if (o.thumbOffset > 0) out.thumbOffset = Math.round(o.thumbOffset);
        if (o.reelOnly) out.shareToFeed = false;
        if (o.trial) out.trialParams = { graduationStrategy: o.trialStrategy || 'MANUAL' };
      }
    }
  } else if (platform === 'facebook') {
    if (o.contentType === 'reel' || o.contentType === 'story') out.contentType = o.contentType;
    if (kind === 'video') {
      if (o.coverUrl && o.contentType !== 'story') out.coverUrl = o.coverUrl;
      if (o.isAiGenerated) out.isAiGenerated = true;
    }
  } else if (platform === 'pinterest') {
    if (kind === 'video' && o.coverUrl) out.coverUrl = o.coverUrl;
  } else if (platform === 'youtube') {
    if (o.ytTitle?.trim()) out.title = o.ytTitle.trim();
    if (o.ytVisibility) out.visibility = o.ytVisibility;
    const tags = (o.ytTagsText || '').split(',').map((t) => t.trim()).filter(Boolean);
    if (tags.length) out.tags = tags;
    if (o.ytMadeForKids) out.madeForKids = true;
    if (o.isAiGenerated) out.containsSyntheticMedia = true;
    if (o.ytFirstComment?.trim()) out.firstComment = o.ytFirstComment.trim();
    if (o.coverUrl) out.coverUrl = o.coverUrl;
  }
  return out;
}

/** Returns a German message or null (mirrors backend rules). */
export function validateExtras(platform, o, kind, imageCount) {
  if (platform === 'instagram') {
    if (o.contentType === 'story' && (kind === 'none' || imageCount > 1)) {
      return 'Instagram-Story: Bitte genau ein Bild oder ein Video verwenden.';
    }
    const collab = (o.collaboratorsText || '').split(/[\s,]+/).map((c) => c.replace(/^@/, '')).filter(Boolean);
    if (collab.length > 3 || collab.some((c) => !/^[A-Za-z0-9._]{1,30}$/.test(c))) {
      return 'Instagram: Maximal 3 Kollaborationen mit gültigem Benutzernamen.';
    }
    if (o.trial && kind !== 'video') return 'Instagram: Trial Reels gibt es nur für Videos.';
  }
  if (platform === 'youtube') {
    if (kind !== 'video') return 'YouTube: Bitte ein Video hochladen.';
    if (/[<>]/.test(o.ytTitle || '')) return 'YouTube-Titel: „<“ und „>“ sind nicht erlaubt.';
    if ((o.ytTitle || '').length > 100) return 'YouTube-Titel: maximal 100 Zeichen.';
    const tags = (o.ytTagsText || '').split(',').map((t) => t.trim()).filter(Boolean);
    if (tags.join('').length > 500) return 'YouTube-Tags: zusammen maximal 500 Zeichen.';
  }
  if (platform === 'facebook' && (o.contentType === 'reel' || o.contentType === 'story')) {
    if (kind === 'none' || imageCount > 1) return `Facebook-${o.contentType === 'reel' ? 'Reel' : 'Story'}: Bitte genau ein Medium verwenden.`;
    if (o.contentType === 'reel' && kind !== 'video') return 'Facebook-Reels brauchen ein Video.';
  }
  return null;
}

export const EMPTY_EXTRAS = {
  contentType: '',
  coverUrl: '',
  thumbOffset: 0,
  reelOnly: false,
  trial: false,
  trialStrategy: 'MANUAL',
  collaboratorsText: '',
  isAiGenerated: false,
  ytTitle: '',
  ytVisibility: 'public',
  ytTagsText: '',
  ytMadeForKids: false,
  ytFirstComment: '',
};

function ToggleRow({ id, label, hint, checked, onChange }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <Label htmlFor={id} className="text-xs text-[#A0AEC0]">{label}</Label>
        {hint && <p className="text-[10px] text-[#5A6C7D] mt-0.5">{hint}</p>}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} data-testid={id} />
    </div>
  );
}

/** Cover for a video: either an uploaded JPG/PNG, or (Instagram) a frame. */
export function CoverPicker({ videoUrl, value, onChange, allowFrame, onUploadCover, durationSec, onDuration }) {
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value.coverUrl ? 'image' : (allowFrame ? 'frame' : 'image'));

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const url = await onUploadCover(file);
      if (url) onChange({ ...value, coverUrl: url, thumbOffset: 0 });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="cover-picker">
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-[#A0AEC0] text-xs">Thumbnail / Cover</Label>
        {allowFrame && (
          <div className="flex text-[10px] rounded overflow-hidden border border-[#1A2B42]">
            {[['frame', 'Frame wählen'], ['image', 'Eigenes Bild']].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setMode(k);
                  if (k === 'frame') onChange({ ...value, coverUrl: '' });
                }}
                className={`px-2 py-0.5 ${mode === k ? 'bg-[#00D9C8]/20 text-[#00D9C8]' : 'text-[#5A6C7D]'}`}
                data-testid={`cover-mode-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {mode === 'frame' && allowFrame && videoUrl ? (
        <>
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            playsInline
            preload="metadata"
            className="w-full max-h-40 object-contain bg-black rounded"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d)) onDuration?.(d);
              e.currentTarget.currentTime = (value.thumbOffset || 0) / 1000;
            }}
          />
          {durationSec > 0 && (
            <>
              <input
                type="range"
                min={0}
                max={Math.max(0, Math.floor(durationSec * 1000))}
                step={100}
                value={Math.min(value.thumbOffset || 0, Math.floor(durationSec * 1000))}
                onChange={(e) => {
                  const ms = Number(e.target.value);
                  onChange({ ...value, thumbOffset: ms, coverUrl: '' });
                  if (videoRef.current) videoRef.current.currentTime = ms / 1000;
                }}
                className="w-full mt-2 accent-[#00D9C8]"
                data-testid="cover-frame-slider"
              />
              <p className="text-[10px] text-[#5A6C7D]">Cover bei {((value.thumbOffset || 0) / 1000).toFixed(1)} s von {durationSec.toFixed(1)} s</p>
            </>
          )}
        </>
      ) : (
        <div>
          {value.coverUrl ? (
            <div className="relative inline-block">
              <img src={value.coverUrl} alt="Cover" className="h-24 rounded border border-[#1A2B42]" data-testid="cover-image-preview" />
              <button
                type="button"
                onClick={() => onChange({ ...value, coverUrl: '' })}
                className="absolute -top-2 -right-2 w-5 h-5 bg-black/80 rounded-full flex items-center justify-center hover:bg-red-500/80"
                data-testid="cover-remove"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 text-xs text-[#A0AEC0] border border-dashed border-[#1A2B42] hover:border-[#00D9C8]/50 rounded px-3 py-2"
              data-testid="cover-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Cover-Bild hochladen (JPG/PNG)
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={pickFile} data-testid="cover-file-input" />
        </div>
      )}
    </div>
  );
}

/** One alt text per image, with AI generation. */
export function AltTextEditor({ imageUrls, value, onChange, onGenerate, maxItems }) {
  const [busy, setBusy] = useState(false);
  const urls = maxItems ? imageUrls.slice(0, maxItems) : imageUrls;
  if (!urls.length) return null;

  const generate = async () => {
    setBusy(true);
    try {
      const res = await onGenerate(urls);
      if (Array.isArray(res)) {
        const next = [...value];
        res.forEach((t, i) => { if (t) next[i] = t; });
        onChange(next);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="alt-text-editor">
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-[#A0AEC0] text-xs">Alt-Texte (Barrierefreiheit &amp; SEO)</Label>
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="flex items-center gap-1 text-[10px] text-[#00D9C8] hover:underline disabled:opacity-50"
          data-testid="alt-text-generate"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          Mit KI erzeugen
        </button>
      </div>
      <div className="space-y-1.5">
        {urls.map((u, i) => (
          <div key={u + i} className="flex items-center gap-2">
            <img src={u} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
            <Input
              value={value[i] || ''}
              onChange={(e) => {
                const next = [...value];
                next[i] = e.target.value.slice(0, 1000);
                onChange(next);
              }}
              placeholder={`Bild ${i + 1}: was ist zu sehen?`}
              className="h-8 text-xs bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
              data-testid={`alt-text-input-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Pinterest's own text fields (replace the generic Text field for pins). */
export function PinterestFields({ pin, onPinChange }) {
  const set = (patch) => onPinChange({ ...pin, ...patch });
  return (
    <div className="space-y-3" data-testid="pinterest-fields">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-[#A0AEC0] text-xs">Titel *</Label>
          <span className={`text-[10px] ${pin.title.length > 100 ? 'text-red-400' : 'text-[#5A6C7D]'}`}>{pin.title.length}/100</span>
        </div>
        <Input value={pin.title} maxLength={100} onChange={(e) => set({ title: e.target.value.slice(0, 100) })}
          className="h-9 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" data-testid="pin-title-input" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-[#A0AEC0] text-xs">Beschreibung</Label>
          <span className={`text-[10px] ${pin.description.length > 500 ? 'text-red-400' : 'text-[#5A6C7D]'}`}>{pin.description.length}/500</span>
        </div>
        <Textarea value={pin.description} maxLength={500} onChange={(e) => set({ description: e.target.value.slice(0, 500) })}
          className="min-h-[80px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none" data-testid="pin-description-input" />
      </div>
      <div>
        <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Link</Label>
        <Input value={pin.link} onChange={(e) => set({ link: e.target.value })}
          className="h-9 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" data-testid="pin-link-input" />
      </div>
    </div>
  );
}

export default function PlatformExtrasPanel({
  platform, kind, value, onChange, videoUrl, imageUrls = [], altTexts = [], onAltTextsChange,
  onUploadCover, onGenerateAlt, durationSec, onDuration, children,
}) {
  const set = (patch) => onChange({ ...value, ...patch });
  const isStory = value.contentType === 'story';
  const showAlt = ALT_TEXT_PLATFORMS.includes(platform) && kind === 'image' && !isStory;
  const titles = { instagram: 'Instagram-Einstellungen', facebook: 'Facebook-Einstellungen', pinterest: 'Pinterest-Einstellungen', youtube: 'YouTube-Einstellungen' };

  return (
    <div className="border border-[#1A2B42] rounded p-3 space-y-3 bg-[#0A1628]/60" data-testid={`${platform}-extras-panel`}>
      <span className="text-xs font-medium text-white">{titles[platform]}</span>

      {children}

      {platform === 'instagram' && (
        <div>
          <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Beitragsart</Label>
          <SimpleSelect
            value={value.contentType}
            onChange={(v) => set({ contentType: v })}
            testId="ig-content-type"
            options={[
              { value: '', label: `${kind === 'video' ? 'Reel' : 'Feed-Beitrag'} (automatisch)` },
              { value: 'story', label: 'Story (genau 1 Bild/Video, 24 h)' },
            ]}
          />
        </div>
      )}
      {platform === 'youtube' && (
        <div className="space-y-3" data-testid="youtube-fields">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-[#A0AEC0] text-xs">Titel</Label>
              <span className={`text-[10px] ${(value.ytTitle || '').length > 100 ? 'text-red-400' : 'text-[#5A6C7D]'}`}>{(value.ytTitle || '').length}/100</span>
            </div>
            <Input value={value.ytTitle} onChange={(e) => set({ ytTitle: e.target.value })}
              className="h-9 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" data-testid="yt-title" />
            <p className="text-[10px] text-[#5A6C7D] mt-1">Der Beitragstext oben wird zur Video-Beschreibung.</p>
          </div>
          <div>
            <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Sichtbarkeit</Label>
            <SimpleSelect value={value.ytVisibility} onChange={(v) => set({ ytVisibility: v || 'public' })} testId="yt-visibility"
              options={[
                { value: 'public', label: 'Öffentlich' },
                { value: 'unlisted', label: 'Nicht gelistet (nur mit Link)' },
                { value: 'private', label: 'Privat' },
              ]} />
          </div>
          <div>
            <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Tags (kommagetrennt)</Label>
            <Input value={value.ytTagsText} onChange={(e) => set({ ytTagsText: e.target.value })}
              className="h-9 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white" data-testid="yt-tags" />
          </div>
          <div>
            <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Erster Kommentar (optional)</Label>
            <Textarea value={value.ytFirstComment} onChange={(e) => set({ ytFirstComment: e.target.value })}
              className="min-h-[60px] bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white resize-none" data-testid="yt-first-comment" />
          </div>
          <div className="divide-y divide-[#1A2B42]">
            <ToggleRow id="yt-kids" label="Für Kinder gemacht"
              hint="Achtung: schaltet Kommentare, Glocke und personalisierte Werbung dauerhaft ab."
              checked={value.ytMadeForKids} onChange={(v) => set({ ytMadeForKids: v })} />
          </div>
          <p className="text-[10px] text-[#5A6C7D]">Shorts: Hochkant- oder Quadrat-Video bis 3 Minuten – YouTube erkennt es automatisch.</p>
        </div>
      )}

      {platform === 'facebook' && (
        <div>
          <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Beitragsart</Label>
          <SimpleSelect
            value={value.contentType}
            onChange={(v) => set({ contentType: v })}
            testId="fb-content-type"
            options={[
              { value: '', label: 'Feed-Beitrag' },
              { value: 'reel', label: 'Reel (1 Video)' },
              { value: 'story', label: 'Story (1 Bild/Video, 24 h)' },
            ]}
          />
        </div>
      )}

      {kind === 'video' && !(platform === 'facebook' && value.contentType === 'story') && !(platform === 'instagram' && isStory) && (
        <CoverPicker
          videoUrl={videoUrl}
          value={value}
          onChange={onChange}
          allowFrame={platform === 'instagram'}
          onUploadCover={onUploadCover}
          durationSec={durationSec}
          onDuration={onDuration}
        />
      )}

      {platform === 'instagram' && !isStory && (
        <>
          {kind === 'video' && (
            <div className="divide-y divide-[#1A2B42]">
              <ToggleRow id="ig-reel-only" label="Nur in Reels zeigen" hint="Das Reel erscheint nicht zusätzlich im Profil-Feed."
                checked={value.reelOnly} onChange={(v) => set({ reelOnly: v })} />
              <ToggleRow id="ig-trial" label="Trial Reel" hint="Wird zuerst Nicht-Followern gezeigt, danach freigegeben."
                checked={value.trial} onChange={(v) => set({ trial: v })} />
            </div>
          )}
          {kind === 'video' && value.trial && (
            <SimpleSelect
              value={value.trialStrategy}
              onChange={(v) => set({ trialStrategy: v })}
              testId="ig-trial-strategy"
              options={[
                { value: 'MANUAL', label: 'Freigabe manuell in Instagram' },
                { value: 'SS_PERFORMANCE', label: 'Automatisch bei guter Performance' },
              ]}
            />
          )}
          <div>
            <Label className="text-[#A0AEC0] text-xs mb-1.5 block">Kollaboration (max. 3 Accounts)</Label>
            <Input
              value={value.collaboratorsText}
              onChange={(e) => set({ collaboratorsText: e.target.value })}
              placeholder="@partner1, @partner2"
              className="h-9 bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white"
              data-testid="ig-collaborators"
            />
            <p className="text-[10px] text-[#5A6C7D] mt-1">Eingeladene müssen die Einladung in Instagram annehmen.</p>
          </div>
        </>
      )}

      {(platform === 'instagram' || platform === 'youtube' || (platform === 'facebook' && kind === 'video')) && (
        <ToggleRow id={`${platform}-ai`} label="KI-generierter Inhalt"
          hint="Fügt das KI-Label der Plattform hinzu."
          checked={value.isAiGenerated} onChange={(v) => set({ isAiGenerated: v })} />
      )}

      {showAlt && (
        <AltTextEditor
          imageUrls={imageUrls}
          value={altTexts}
          onChange={onAltTextsChange}
          onGenerate={onGenerateAlt}
          maxItems={platform === 'pinterest' ? 1 : undefined}
        />
      )}
    </div>
  );
}
